// api/businessApi.js
import axiosInstance from './axiosConfig';
import ApiConfig from './config';
import { ConflictError } from './errors';

export const getBusinessTypes = async () => {
  try {
    const response = await axiosInstance.get(ApiConfig.ENDPOINTS.GET_BUSINESS_TYPES);
    return response.data;
  } catch (error) {
    return [];
  }
};

export const getBusinesses = async () => {
  try {
    const response = await axiosInstance.get(ApiConfig.ENDPOINTS.GET_BUSINESSES, {
      params: { resellerId: '' }
    });

    let businesses = response.data;
    if (!businesses || businesses.length === 0) {
      return [];
    }

    return businesses.map((business, index) => ({
      id: business.businessGuid || business.id || `business-${index}`,
      businessGuid: business.businessGuid,
      acquirerMId: business.acquirerMId || `MCH-${String(index + 1).padStart(4, '0')}`,
      companyName: business.companyName || 'Unnamed Business',
      tradingName: business.tradingName || business.companyName || '',
      directorName: business.directorName || '',
      businessAddress: business.businessAddress || '',
      businessType: business.typeDesc || business.businessTypeName || business.businessType,
      businessTypeValue: business.businessType,
      mobile: business.mobile || '',
      email: business.email || '',
      pFeeCap: business.pFeeCap !== undefined ? String(business.pFeeCap) : '0',
      dFeeCap: business.dFeeCap !== undefined ? String(business.dFeeCap) : '0',
      isActive: business.isActive !== undefined ? business.isActive : true,
      lastUpdate: business.lastUpdate,
      terminals: business.terminals || [],
      version: business.version || 0,
    }));
  } catch (error) {
    return [];
  }
};

export const getBusinessById = async (businessId) => {
  if (!businessId) {
    throw new Error('Business ID is required');
  }

  try {
    const response = await axiosInstance.get(ApiConfig.ENDPOINTS.GET_BUSINESS, {
      params: { businessId }
    });

    let businessData = response.data;
    if (!businessData || !businessData.businessGuid) {
      throw new Error('Invalid business data received');
    }

    return {
      id: businessData.businessGuid || businessData.id || businessId,
      businessGuid: businessData.businessGuid,
      timezone: businessData.timezone,
      acquirerMId: businessData.acquirerMId || '',
      companyName: businessData.companyName || 'Unnamed Business',
      tradingName: businessData.tradingName || businessData.companyName || '',
      directorName: businessData.directorName || '',
      businessAddress: businessData.businessAddress || '',
      businessType: businessData.businessType || businessData.typeDesc || '',
      businessTypeValue: businessData.businessType,
      mobile: businessData.mobile || '',
      email: businessData.email || '',
      pFeeCap: businessData.pFeeCap !== undefined ? String(businessData.pFeeCap) : '0',
      dFeeCap: businessData.dFeeCap !== undefined ? String(businessData.dFeeCap) : '0',
      latLng: businessData.latLng || { lat: 0, lng: 0 },
      isActive: businessData.isActive !== undefined ? businessData.isActive : true,
      lastUpdate: businessData.lastUpdate,
      terminals: businessData.terminals || [],
      version: businessData.version || 0,
      hasTerminals: businessData.hasTerminals,
    };
  } catch (error) {
    throw error;
  }
};

export const addBusiness = async (businessData) => {
  try {
    const payload = {
      companyName: businessData.companyName,
      tradingName: businessData.tradingName || businessData.companyName,
      directorName: businessData.directorName,
      businessAddress: businessData.businessAddress,
      businessType: businessData.businessType,
      mobile: businessData.mobile,
      email: businessData.email,
      acquirerMId: businessData.acquirerMId,
      pFeeCap: parseFloat(businessData.pFeeCap) || 0,
      dFeeCap: parseFloat(businessData.dFeeCap) || 0,
      latLng: businessData.latLng || { lat: 0, lng: 0 },
      version: 0,
      isActive: true,
    };

    const response = await axiosInstance.post(ApiConfig.ENDPOINTS.ADD_BUSINESS, payload);
    return response.data;
  } catch (error) {
    // Check for 409 conflict
    if (error.response?.status === 409) {
      const data = error.response.data || {};
      throw new ConflictError(
        data.detail || data.message || 'Conflict detected',
        data
      );
    }
    throw error;
  }
};

export const updateBusiness = async (id, businessData) => {
  try {
    const payload = {
      businessGuid: id,
      companyName: businessData.companyName,
      tradingName: businessData.tradingName || businessData.companyName,
      directorName: businessData.directorName,
      businessAddress: businessData.businessAddress,
      businessType: businessData.businessType,
      mobile: businessData.mobile,
      email: businessData.email,
      acquirerMId: businessData.acquirerMId,
      pFeeCap: parseFloat(businessData.pFeeCap) || 0,
      dFeeCap: parseFloat(businessData.dFeeCap) || 0,
      latLng: businessData.latLng !== undefined && businessData.latLng !== null
        ? businessData.latLng
        : null,
      version: businessData.version,
      isActive: businessData.isActive !== undefined ? businessData.isActive : true,
    };

    const response = await axiosInstance.put(ApiConfig.ENDPOINTS.UPDATE_BUSINESS, payload);
    return response.data;
  } catch (error) {
    // Check for 409 conflict
    if (error.response?.status === 409) {
      const conflictError = new ConflictError(
        error.response?.data?.message || 'Conflict detected',
        error.response?.data
      );
      throw conflictError;
    }
    throw error;
  }
};